import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { destroySession } from "../../helpers/Session";
import DataTable from "react-data-table-component";
import EventsLoader from "../../components/loader/EventsLoader";
import toast from "../../components/sweetalert/Sweetalert";
import Swal from "sweetalert2";

const Events = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken') || '';
    const uname = sessionStorage.getItem('uname') || '';

    const [currentPage, setCurrentPage] = useState(1);
    const [rowPerPage, setRowPerPage] = useState(10);

    const [formSubmit, setFormSubmit] = useState(false);
    const [formData, setFormData] = useState({
        id: 0,
        event: '',
        eventdate: '',
        eventdateraw: '',
        status: ''
    });

    const eventDataKey = ['events', currentPage, rowPerPage];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    }

    const resetForm = () => {
        setFormData({
            id: 0,
            event: '',
            eventdate: '',
            eventdateraw: '',
            status: ''
        });
    }

    const tableColumns = [
        { name: 'Event', selector: row => row.event },
        { name: 'Event Date', selector: row => row.eventdate },
        { name: 'Status', selector: row => row.status },
        { name: 'Action', selector: row => row.action }
    ];

    const deleteEventMutation = useMutation({
        mutationFn: async (id) => {
            const res = await axios.delete(`events/${id}?uname=${uname}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(eventDataKey);
            toast.fire({ icon: 'success', title: data });
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.response || 'Something went wrong while deleting event!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const deleteEvent = (id, event) => {
        Swal.fire({
            text: `Are you sure you want to delete event details of ${event}?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: (result) => {
				return new Promise(function(resolve) {
                    if (result) {
                        deleteEventMutation.mutate(id);
                        resolve();
                    }
                });
            }
        });
    };

    const { data: events, isLoading: isLoadingEvents, error: errorEvents } = useQuery({
        queryKey: eventDataKey,
        queryFn: async () => {
            const res = await axios.get(`events?page=${currentPage}&rows_per_page=${rowPerPage}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            const data = res.data.response?.events?.map(item => ({
                event: item.event,
                eventdate: item.eventdate,
                status: item.status === 1 ? <span className="label label-success">Active</span> : <span className="label label-danger">Inactive</span>,
                action: (
                    <div className="buttons">
                        <button type="button" className="btn-edit" onClick={() => setFormData({ id: item.id, event: item.event, eventdate: item.eventdate, eventdateraw: item.eventdateraw, status: item.status })}>
                            <i className="fa fa-edit"></i>
                        </button>
                        <button type="button" className="btn-delete" onClick={() => deleteEvent(item.id, item.event)}>
                            <i className="fa fa-trash"></i>
                        </button>
                    </div>
                )
            }));

            return { data, totalRows: res.data.response?.totalRows }
        },
        enabled: !!accesstoken,
        retry: 1
    });

    useEffect(() => {
        if (errorEvents) {
            if (errorEvents.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleRowsPerPageChange = (newPerPage) => {
        setRowPerPage(newPerPage);
        setCurrentPage(1);
    };

    const submitEventMutation = useMutation({
        mutationFn: async (eventData) => {
            const res = await axios.post('events', eventData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            resetForm();
            setFormSubmit(false);
            queryClient.invalidateQueries(eventDataKey);
            toast.fire({ icon: 'success', title: data });
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMessage = error.response?.data?.response || 'Something went wrong while saving event details!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();               
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleSubmitEvent = () => {
        const { event, eventdateraw, status } = formData;

        if (!event || !eventdateraw || !status) {
            toast.fire({ icon: 'error', title: "All fields are required." });
            return;
        }

        setFormSubmit(true);
        submitEventMutation.mutate({ ...formData, uname });
    };

    return (
        <div className="events">
            <div className="page-header">
                <div className="title">
                    <h1>Events</h1>
                    <ul className="breadcrumbs">
                        <li>
                            <NavLink to="/settings/devices">
                                Settings
                            </NavLink>
                        </li>
                        <li>
                            <span>Events</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="panels">
                <div className='left-panel'>
                    <DataTable 
                        columns={tableColumns} 
                        data={events?.data || []} 
                        progressPending={isLoadingEvents} 
                        progressComponent={<EventsLoader />} 
                        pagination 
                        paginationServer 
                        paginationTotalRows={events?.totalRows || 0} 
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        onChangePage={handlePageChange}
                        dense 
                        highlightOnHover 
                        responsive 
                    />
                </div>

                <div className='right-panel'>
                    <form>
                        <div className='form-group'>
                            <label>Event</label>
                            <input type="text" name="event" value={formData.event} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Event Date</label>
                            <input type='date' name='eventdateraw' value={formData.eventdateraw} onChange={handleInputChange} disabled={formSubmit} />
                        </div>
                        <div className='form-group'>
                            <label>Status</label>
                            <select name='status' value={formData.status} onChange={handleInputChange} disabled={formSubmit}>
                                <option value=''></option>
                                <option value='1'>Active</option>
                                <option value='2'>Inactive</option>
                            </select>
                        </div>
                        <div className='button-group'>
                            <button type='button' onClick={handleSubmitEvent} disabled={formSubmit}>
                                {formSubmit ? <span>SAVING <i className="fa fa-spinner fa-spin"></i></span> : 'SAVE'}
                            </button>
                            <button type='button' onClick={resetForm} disabled={formSubmit}>RESET</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default Events;